import { Shape, ShapeCombo, ShapeComboOptions } from "../Shape";
import { Color } from "../../../color";
import { toDegree, toRadian } from "../../../math";
import RectShape from "./RectShape";
// import StarShape from "./StarShape";

// https://www.kirilv.com/canvas-confetti/
// https://github.com/catdad/canvas-confetti

export type ConfettiShapeComboOptions = Partial<{
  shapeCreator: () => Shape;
  width: number;
  height: number;
  x: number;
  y: number;
  particleCount: number;
  angle: number;
  spread: number;
  startVelocity: number;
  decay: number;
  gravity: number;
  drift: number;
  ticks: number;
  colors: string[];
  scalar: number;
  flat: boolean;
}> &
  ShapeComboOptions;

export class ConfettiShapeCombo extends ShapeCombo {
  private options: ConfettiShapeComboOptions;

  constructor(options: ConfettiShapeComboOptions) {
    super(options);

    this.options = options;

    const {
      shapeCreator,
      width,
      height,
      x,
      y,
      particleCount,
      angle,
      spread,
      startVelocity,
      decay,
      gravity,
      drift,
      ticks,
      colors,
      scalar,
      flat
    } = Object.assign(
      {
        shapeCreator: () => new RectShape({ rectWidth: 10, rectHeight: 10 }),
        width: 100,
        height: 100,
        x: 0.5,
        y: 0.6,
        particleCount: 100,
        angle: 90,
        spread: 70,
        startVelocity: 45,
        decay: 0.9,
        gravity: 1,
        drift: 0,
        ticks: 200,
        colors: Color.getColors(),
        scalar: 1,
        flat: false
      },
      options || {}
    );
    const addShapeAttributes = (s: Shape, temp: number) => {
      // 通用属性
      s.x = width * x;
      s.y = height * y;
      s.scaleX = scalar;
      s.scaleY = scalar;
      const fillColor = colors[temp % colors.length];
      s.setAttr({
        fill: fillColor
      });
      s.interactive = false;
      // 自定义属性
      s.wobble = Math.random() * 10;
      s.wobbleSpeed = Math.min(0.11, Math.random() * 0.1 + 0.05);
      s.velocity = startVelocity * 0.5 + Math.random() * startVelocity;
      s.angle2D = -radAngle + (0.5 * radSpread - Math.random() * radSpread);
      s.tiltAngle = (Math.random() * (0.75 - 0.25) + 0.25) * Math.PI;
      s.tick = 0;
      s.totalTicks = ticks;
      s.decay = decay;
      s.drift = drift;
      s.random = Math.random() + 2;
      s.tiltSin = 0;
      s.tiltCos = 0;
      s.wobbleX = 0;
      s.wobbleY = 0;
      s.gravity = gravity * 3;
      s.scalar = scalar;
      s.flat = flat;
      s.color = new Color(fillColor);
    };
    const radAngle = toRadian(angle);
    const radSpread = toRadian(spread);
    let temp = particleCount;
    while (temp) {
      const s = shapeCreator();
      addShapeAttributes(s, temp);
      // s.clone()可以改写，用以复制「自定义属性」
      s.clone = ((temp) => () => {
        const sc = shapeCreator();
        addShapeAttributes(sc, temp);
        return sc;
      })(temp);
      this.addShape(s);
      temp--;
    }
  }

  protected cloneShapeCombo(): ConfettiShapeCombo {
    const shapeCombo = new ConfettiShapeCombo(this.options);
    return shapeCombo;
  }

  // 提供外部变更
  public update() {
    this.shapes = this.shapes.filter((fetti) => {
      fetti.x += Math.cos(fetti.angle2D) * fetti.velocity + fetti.drift;
      fetti.y += Math.sin(fetti.angle2D) * fetti.velocity + fetti.gravity;
      fetti.velocity *= fetti.decay;

      if (fetti.flat) {
        fetti.wobble = 0;
        fetti.wobbleX = fetti.x + 10 * fetti.scalar;
        fetti.wobbleY = fetti.y + 10 * fetti.scalar;
        fetti.tiltSin = 0;
        fetti.tiltCos = 0;
        fetti.random = 1;
      } else {
        fetti.wobble += fetti.wobbleSpeed;
        fetti.wobbleX = fetti.x + 10 * fetti.scalar * Math.cos(fetti.wobble);
        fetti.wobbleY = fetti.y + 10 * fetti.scalar * Math.sin(fetti.wobble);
        fetti.tiltAngle += 0.1;
        fetti.tiltSin = Math.sin(fetti.tiltAngle);
        fetti.tiltCos = Math.cos(fetti.tiltAngle);
        fetti.random = Math.random() + 2;
      }

      const progress = fetti.tick++ / fetti.totalTicks;
      const x1 = fetti.x + fetti.random * fetti.tiltCos;
      const y1 = fetti.y + fetti.random * fetti.tiltSin;
      const x2 = fetti.wobbleX + fetti.random * fetti.tiltCos;
      const y2 = fetti.wobbleY + fetti.random * fetti.tiltSin;

      fetti.setAttr("fill", fetti.color.setAlpha(1 - progress).toString());
      fetti.degree = toDegree((Math.PI / 10) * fetti.wobble);
      fetti.scaleX = Math.abs(x2 - x1) * 0.1;
      fetti.scaleY = Math.abs(y2 - y1) * 0.1;

      return fetti.tick < fetti.totalTicks;
    });

    // if can continue
    return this.shapes.length > 0;
  }
}

export default ConfettiShapeCombo;
